import { Component, OnInit, Input, AfterViewInit, Output, EventEmitter } from '@angular/core';
import { LoggerService } from '@wdpr/ra-angular-logger';
import { interval, Subscription } from 'rxjs';
import { AsyncScheduler } from 'rxjs/internal/scheduler/AsyncScheduler';
import { asyncScheduler } from 'rxjs/internal/scheduler/async';
import get from 'lodash-es/get';
import moment from 'moment';

import { CountdownClock } from './countdown-clock.interface';
import { COUNTDOWN_CLOCK_ANIMATIONS } from './countdown-clock.animations';
import { CountupOptions } from '@finder/shared/directives/countup/countup.interface';
import { CommonModule } from '@angular/common';
import { SafeHtmlPipe } from '@finder/shared/utils/pipes/safe-html/safe-html.pipe';
import { CountupDirective } from '@finder/shared/directives/countup/countup.directive';
import { InViewportDirective } from '@finder/shared/directives/in-viewport/in-viewport.directive';
import { LottieComponent } from '../lottie/lottie.component';

const CLASS_NAME = 'Countdown Clock';

@Component({
    selector: 'finder-countdown-clock',
    templateUrl: './countdown-clock.component.html',
    styleUrls: ['./countdown-clock.component.scss'],
    animations: [...COUNTDOWN_CLOCK_ANIMATIONS],
    standalone: true,
    imports: [
        CommonModule,
        SafeHtmlPipe,
        CountupDirective,
        InViewportDirective,
        LottieComponent
    ]
})
export class CountdownClockComponent implements OnInit, AfterViewInit {

    @Input() countdownClockData: CountdownClock;
    @Input() revealClock: boolean;
    @Output() clockVisible = new EventEmitter<boolean>();

    dateData;
    formatArray: string[];
    style: any = {};
    title: string;
    expired = false;
    showCountup = false;

    private countDownTimer$: Subscription;
    private nowDay;
    private targetDay;

    constructor(
        private logger: LoggerService,
        private scheduler: AsyncScheduler = asyncScheduler
    ) {}

    ngOnInit() {
        // Check for clock reveal
        this.revealClock = this.countdownClockData.delayedReveal !== true;
        // Setup countup
        this.setupCountup();
        // Setup the data once
        this.setupCountDownData();
        // Setup the data on the display object
        this.getClock();
        // Make sure we have the correct time
        this.moveTimeForward();
    }

    ngAfterViewInit(): void {
        // Check to see if the dates are valid
        if (this.expired) {
            return;
        }

        // Create interval to update time
        // Use the injected scheduler so we can mock this in the test
        this.countDownTimer$ = interval(1000, this.scheduler).subscribe(val => {
            this.moveTimeForward();
        });
    }

    /**
     * @name        trackByNumber
     * @description Create an index so the page doesn't constantly redraw
     * @param index index of the number
     * @param num number from the array
     */
    trackByNumber(index: number, num: string) {
        const hash = `${index}:${num}`;

        return hash;
    }

    /**
     * @name        getPosition
     * @description Add the digit position to the dom element. Used for animation
     * @param index index of the number
     * @param format date format
     */
    getPosition(index, format) {
        return this.dateData[format].values.length - index;
    }

    /**
     * @name        getAriaClock
     * @description Update the countdown aira label
     */
    getAriaClock() {
        // Throw away the seconds if they exist
        const ariaArray = this.formatArray.filter(format => format !== 'ss')
            .map(format => `${this.dateData[format].value} ${this.dateData[format].label}`);

        return ariaArray.join(', ');
    }

    setupCountup() {
        this.showCountup = this.countdownClockData.countupEnabled || false;
    }

    getCountupData(data): CountupOptions {
        return {
            value: data.value,
            animationDuration: this.countdownClockData.animationDuration || null,
        };
    }

    countupComplete(e) {
        const val = e === 'true';
        this.showCountup = val;
        this.style.visibility = val ? 'hidden' : 'initial';
    }

    setClockVisible() {
        this.revealClock = true;
        this.clockVisible.emit(true);
    }

    /**
     * @name        moveTimeForward
     * @description Update the object containing the countdown timer information
     */
    private moveTimeForward() {
        // If we've hit the target time just stop
        if (this.isTargetDateExpired()) {
            this.expired = true;
            this.logger.log(`${CLASS_NAME} -- reached the target time`);

            // The countdown timer may not have been created so we need to check before unsubscribing
            if (this.countDownTimer$) {
                this.countDownTimer$.unsubscribe();
            }

            return;
        }

        // Move the clock forward
        this.nowDay.add(1, 'seconds');

        // Get clock data
        this.getClock();
    }

    /**
     * @name        setupCountDownData
     * @description Take the data passed in and format it for use
     */
    private setupCountDownData() {
        this.formatArray = get(this.countdownClockData, 'format', '').split(':');
        this.nowDay = moment();
        this.targetDay = moment(get(this.countdownClockData, 'targetDay', ''));
        this.title = get(this.countdownClockData, 'title', '');
        this.style = {
            backgroundColor: get(this.countdownClockData, 'clockBackgroundColor', ''),
            color: get(this.countdownClockData, 'clockFontColor', 'white'),
            visibility: this.showCountup ? 'hidden' : 'initial',
        };

        // The position code from the CT codebase is used when the countdown timer is placed in the hero

        // Check to see if our target date is valid
        if (this.areDatesInvalid()) {
            this.logger.error(`${CLASS_NAME} -- targetDay is not a valid time`);

            return;
        }

        // Check to see if the time has expired
        if (this.isTargetDateExpired()) {
            this.expired = true;
            this.logger.error(`${CLASS_NAME} -- the countdown timer has expired`);

            return;
        }

        /**
         * The data object tracking the time needs to be broken down in this manor so
         * change detection will work properly to just redraw one of the numbers instead
         * of both
         */
        this.dateData = {
            ww: {
                label: get(this.countdownClockData.labels, 'weeks'),
                value: 0,
                values: [] // This is the value number changed to an array of numbers
            },
            dd: {
                label: get(this.countdownClockData.labels, 'days'),
                value: 0,
                values: []
            },
            hh: {
                label: get(this.countdownClockData.labels, 'hours'),
                value: 0,
                values: []
            },
            mm: {
                label: get(this.countdownClockData.labels, 'minutes'),
                value: 0,
                values: []
            },
            ss: {
                label: get(this.countdownClockData.labels, 'seconds'),
                value: 0,
                values: []
            }
        };
    }

    /**
     * @name        isTargetDateExpired
     * @description Checks to see if the count down timer can be updated with the current dates
     * @returns     Boolean
     */
    private isTargetDateExpired() {
        return this.nowDay.isSame(this.targetDay) || this.nowDay.isAfter(this.targetDay);
    }

    /**
     * @name        areDatesInvalid
     * @description Make sure both date objects are valid
     * @returns     Boolean
     */
    private areDatesInvalid() {
        // Make sure both date objects are valid
        const result = !(this.nowDay.isValid() && this.targetDay.isValid());

        // Expire the time right away if needed
        this.expired = result;

        return result;
    }

    /**
     * @name        getClock
     * @description Update the object that contains the countdown information
     */
    private getClock() {
        // Check to see if we should process the clock data
        if (this.expired) {
            return;
        }
        // Clone the real day so we can adjust it without side affects
        const nowDayClone = this.nowDay.clone();

        /**
         * We need to check for the largest to the smallest format and adjust
         * the date as we go
         */

        // Check for weeks
        if (this.formatArray.includes('ww')) {
            Object.assign(this.dateData.ww, {
                value: this.targetDay.diff(nowDayClone, 'weeks'),
                values: this.dateData.ww.value.toString().padStart(2, '0').split('')
            });
        }
        // Check for days
        if (this.formatArray.includes('dd')) {
            Object.assign(this.dateData.dd, {
                value: this.targetDay.diff(nowDayClone.add(this.dateData.ww.value, 'weeks'), 'days'),
                values: this.dateData.dd.value.toString().padStart(2, '0').split('')
            });
        }
        // Check for hours
        if (this.formatArray.includes('hh')) {
            Object.assign(this.dateData.hh, {
                value: this.targetDay.diff(nowDayClone.add(this.dateData.dd.value, 'days'), 'hours'),
                values: this.dateData.hh.value.toString().padStart(2, '0').split('')
            });
        }
        // Check for minutes
        if (this.formatArray.includes('mm')) {
            Object.assign(this.dateData.mm, {
                value: this.targetDay.diff(nowDayClone.add(this.dateData.hh.value, 'hours'), 'minutes'),
                values: this.dateData.mm.value.toString().padStart(2, '0').split('')
            });
        }
        // Check for seconds
        if (this.formatArray.includes('ss')) {
            Object.assign(this.dateData.ss, {
                value: this.targetDay.diff(nowDayClone.add(this.dateData.mm.value, 'minutes'), 'seconds'),
                values: this.dateData.ss.value.toString().padStart(2, '0').split('')
            });
        }
    }
}
